import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../../Styles";
import { FieldStyles, FieldTypes, FilterDisplayType } from "../../../Definitions";
import { AICO_IO_Data } from "../../../Assets/TestData/controllers";
import { Value } from "baseui/select";
import { InputField, SelectField } from "../../BaseComponents";
import { Pen, Section } from "./newSectionModal";

export const ioParameters: FilterDisplayType[] = AICO_IO_Data.map(io => ({
  label: io.name,
  id: io.id,
}));

interface NewSectionModalLvl2_Props {
  // pens: Pen[];
  section: Section;
  onSectionChange: (updatedSection: Section) => void;
}

const NewSectionModalLvl2: React.FC<NewSectionModalLvl2_Props> = ({ section, onSectionChange }) => {
  const { t } = useTranslation();
  const [pensWithFeederOutput, setPensWithFeederOutput] = useState(0);
  const [pensWithWaterOutput, setPensWithWaterOutput] = useState(0);
  const [pensWithActivatorInput, setPensWithActivatorInput] = useState(0)

  // Total number of pens
  const activePensCount = section.pens.filter((pen) => pen.penId !== null).length;

  const totalOutputs = section.water ? activePensCount*2 : activePensCount
  const totalInputs = section.activator ? activePensCount : 0
  const remainingOutputs = section.water ? totalOutputs - pensWithFeederOutput - pensWithWaterOutput : totalOutputs - pensWithFeederOutput 
  const remainingInputs = section.activator ? totalInputs - pensWithActivatorInput : 0

  // #region | Handle IO functions
  // SelectField Options
  const ioOptions = ioParameters.map((type) => ({
    label: type.label, 
    id: type.id
  }));

  // Handler for select changes in IO
  const handlePenIOChange = (penId: string, value: Value) => {
    const updatedPens = section.pens.map((pen) =>
      pen.id === penId
        ? { ...pen, io: value[0]?.id ? String(value[0]?.id) : null }
        : pen
    );
    onSectionChange({ ...section, pens: updatedPens });
  };

  // Handler for input changes in feederOutput
  const handleFeederOutPutChange = (penId: string, newValue: string) => {
    const updatedPens = section.pens.map((pen) => {
      if (pen.id === penId) {
        return {
          ...pen,
          feederOutput: newValue ? Number(newValue) : null,
        };
      }
      return pen;
    });
  
    onSectionChange({ ...section, pens: updatedPens });
    setPensWithFeederOutput(updatedPens.filter(pen => pen.feederOutput !== null).length); // Update the count
  };

  // Handler for input changes in waterOutput
  const handleWaterOutPutChange = (penId: string, newValue: string) => {
    const updatedPens = section.pens.map((pen) => {
      if (pen.id === penId) {
        return {
          ...pen,
          waterOutput: newValue ? Number(newValue) : null,
        };
      }
      return pen;
    });
  
    onSectionChange({ ...section, pens: updatedPens });
    setPensWithWaterOutput(updatedPens.filter(pen => pen.waterOutput !== null).length); // Update the count
  };

  // Handler for input changes in activatorInput
  const handleActivatorInPutChange = (penId: string, newValue: string) => {
    const updatedPens = section.pens.map((pen) => {
      if (pen.id === penId) {
        return {
          ...pen,
          activatorInput: newValue ? Number(newValue) : null,
        };
      }
      return pen;
    });
  
    onSectionChange({ ...section, pens: updatedPens });
    setPensWithActivatorInput(updatedPens.filter(pen => pen.activatorInput !== null).length); // Update the count
  };

  // Handler for reset action when delete icon is clicked
  const handleResetPenIO = (penId: string) => {
    const updatedPens = section.pens.map((pen) => {
      if (pen.id === penId) {
        // Nulstil io og relaterede værdier
        const updatedPen: Pen = {
          ...pen,
          io: null, // Reset IO
          feederOutput: null, // Reset feederOutput
        };

        // Nulstil waterOutput og activatorInput, hvis de eksisterer
        if (updatedPen.hasOwnProperty("waterOutput")) {
          updatedPen.waterOutput = null;
        }
        if (updatedPen.hasOwnProperty("activatorInput")) {
          updatedPen.activatorInput = null;
        }

        return updatedPen;
      }
      return pen;
    });

    onSectionChange({ ...section, pens: updatedPens });

    // Opdater tællere
    setPensWithFeederOutput(updatedPens.filter((pen) => pen.feederOutput !== null).length);
    setPensWithWaterOutput(updatedPens.filter((pen) => pen.waterOutput !== null).length);
    setPensWithActivatorInput(updatedPens.filter((pen) => pen.activatorInput !== null).length);
  };
  // #endregion

  useEffect(() => {
    section.pens.map((pen, index) => {
      if (pen.feederOutput === null) {
        handleFeederOutPutChange(pen.id, 1 + '');
      }
    })
  }, []);

  // #region | IO Fields
  const ioFields = useMemo(() => {
    const get_DataSelection = (pen: Pen, index:number, property:string):string|undefined => {
      var value = pen.hasOwnProperty(property)?(pen as any)[property]:null;
      
      return "1"
    }
    return section.pens.map((pen, index) => {
      const ioFields = (
        <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "start", gap: "9px"}}>
          <div>
            <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.feederOutput")}</p>
            <InputField placeholder={"0..."} inputType="number" fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.mediumInputField} textStart={true} context={get_DataSelection(pen, index, "feederOutput")} onChange={(newValue) => handleFeederOutPutChange(pen.id, newValue)} minValue={1}/>
          </div> 
          {section.water && (
            <div>
              <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.waterOutput")}</p>
              <InputField placeholder={"0..."} inputType="number" fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.mediumInputField} textStart={true} onChange={(newValue) => handleWaterOutPutChange(pen.id, newValue)} minValue={1}/>
            </div>
          )}
          {section.activator && (
            <div>
              <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary, padding: "0px 0px 2px 22px" }}>{t("locationFeeder.newSectionPopup.activatorInput")}</p>
              <InputField placeholder={"0..."} inputType="number" fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.mediumInputField} textStart={true} onChange={(newValue) => handleActivatorInPutChange(pen.id, newValue)} minValue={1}/>
            </div>
          )}
        </div>
      );
    
      return (
        <div key={pen.id} style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
          <div style={{display: "flex", flexDirection: "column", gap: "2px"}}>
            <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, paddingLeft: "22px"}}>
              {t("locationFeeder.newSectionPopup.pen")} {pen.penId}
            </p>
            <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "center", justifyContent: "start" }}>
              <SelectField
                key={pen.id}
                placeholder={t("locationFeeder.newSectionPopup.chooseIO")+"..."}
                options={ioOptions}
                onChange={(value) => handlePenIOChange(pen.id, value)}
                overrides={{
                  textStart: true
                }}
              />
              <div style={{cursor: "pointer" }} onClick={() => handleResetPenIO(pen.id)}>
                <glyphs.Delete size="25px"/>
              </div>
            </div>
          </div>
          <div style={{
            width: "calc(100% - 20px)",
            height: "fit-content",
            backgroundColor: colors.greyLightSecondary,
            padding: "10px 10px",
            borderRadius: "10px"}}>
            {ioFields}  
          </div>
        </div>
      );
    })
  }, [section])
  // #endregion

  return (
    <div style={{ width: "600px" }}>
      <div
        style={{
          borderTop: "2px solid " + colors.blueDarkSecondary,
          padding: "10px 10px",
          maxHeight: "40vh",
          overflowY: "scroll",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: colors.blueDarkSecondary + colors.transparent,
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
          flexDirection: "column",
          gap: "15px"
        }}
      >
        {ioFields}  
      </div>
      <div style={{borderTop: "2px solid"+colors.blueDarkSecondary, borderBottom: "2px solid"+colors.blueDarkSecondary, padding: "10px", maxHeight: "200px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{t("locationFeeder.newSectionPopup.lanController")+":"}</p>
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{section.lanController} </p>
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{t("locationFeeder.newSectionPopup.remainingOutputs")+":"}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingOutputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingOutputs}</p>
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{t("locationFeeder.newSectionPopup.remainingInputs")+":"}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingInputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingInputs}</p>
        </div>
      </div>
    </div>
  );
};

export default NewSectionModalLvl2;


